import { Component, OnDestroy, OnInit } from '@angular/core'
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop'
import { CrmAssignee, Prospect, ProspectSource, ProspectSourceLabels, ProspectStatus, ProspectStatusColors, ProspectStatusLabels, ProspectType, ProspectTypeLabels } from '@parkupp/core'
import { Subscription } from 'rxjs'
import { ProspectingService } from 'src/app/services/prospecting.service'
import { ProspectModalService } from '../prospect-modal/prospect-modal.service'

interface BoardColumn {
    status: ProspectStatus
    label: string
    color: string
    prospects: Prospect[]
}

@Component({
    selector: 'app-admin-prospecting-board',
    templateUrl: './admin-prospecting-board.component.html',
    styleUrls: ['./admin-prospecting-board.component.scss'],
})
export class AdminProspectingBoardComponent implements OnInit, OnDestroy {
    private static readonly PIPELINE: ProspectStatus[] = [
        ProspectStatus.NEW,
        ProspectStatus.PROSPECTING,
        ProspectStatus.CONTACTED,
        ProspectStatus.NEGOTIATING,
        ProspectStatus.CLIENT,
    ]

    allProspects: Prospect[] = []
    columns: BoardColumn[] = []
    connectedLists: string[] = []
    showArchived = false
    loading = true

    assigneeFilter = ''
    sourceFilter = ''
    searchTermFilter = ''
    prospectTypeFilter = ''
    assignees: CrmAssignee[] = []
    sourceOptions = Object.values(ProspectSource)
    sourceLabels = ProspectSourceLabels
    prospectTypeOptions = Object.values(ProspectType)
    prospectTypeLabels = ProspectTypeLabels
    searchTermOptions: string[] = []

    private sub: Subscription | null = null

    constructor(private prospecting: ProspectingService, private modal: ProspectModalService) {}

    async ngOnInit(): Promise<void> {
        this.prospecting.listProspectingAssignees().subscribe((a) => (this.assignees = a))
        try {
            this.allProspects = await this.prospecting.listProspects()
            this.searchTermOptions = [...new Set(this.allProspects.map((p) => p.searchTerm).filter(Boolean) as string[])].sort()
            this.rebuild()
        } catch (e) {
            console.error('Error loading prospects:', e)
        } finally {
            this.loading = false
        }
    }

    applyFilters(): void {
        this.rebuild()
    }

    ngOnDestroy(): void {
        this.sub?.unsubscribe()
    }

    toggleArchived(): void {
        this.showArchived = !this.showArchived
        this.rebuild()
    }

    private rebuild(): void {
        const statuses = this.showArchived
            ? [...AdminProspectingBoardComponent.PIPELINE, ProspectStatus.ARCHIVED]
            : [...AdminProspectingBoardComponent.PIPELINE]

        const filtered = this.allProspects.filter((p) => {
            if (this.assigneeFilter && p.assignedToUid !== this.assigneeFilter) return false
            if (this.sourceFilter && p.source !== this.sourceFilter) return false
            if (this.searchTermFilter && p.searchTerm !== this.searchTermFilter) return false
            if (this.prospectTypeFilter && p.prospectType !== this.prospectTypeFilter) return false
            return true
        })

        this.connectedLists = statuses.map((s) => this.columnId(s))
        this.columns = statuses.map((status) => ({
            status,
            label: ProspectStatusLabels[status],
            color: ProspectStatusColors[status],
            prospects: filtered
                .filter((p) => p.status === status)
                .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)),
        }))
    }

    async onDrop(event: CdkDragDrop<Prospect[]>, targetStatus: ProspectStatus): Promise<void> {
        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex)
            return
        }
        const prospect = event.previousContainer.data[event.previousIndex]
        transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex)
        if (prospect?.$key) {
            try {
                await this.prospecting.updateProspectStatus(prospect.$key, targetStatus)
            } catch (e) {
                console.error('Failed to update prospect status', e)
            }
        }
    }

    openProspect(p: Prospect): void {
        if (p.$key) this.modal.open(p.$key)
    }

    nearestLabel(p: Prospect): string {
        if (!p.nearestParkings || !p.nearestParkings.length) return ''
        const n = p.nearestParkings[0]
        return `${n.distanceKm} km - ${n.parkingName}`
    }

    columnId(status: ProspectStatus): string {
        return 'prospect-board-' + status.toLowerCase()
    }
}
